/* eslint-disable no-console */
import { delMany, getMany, setMany } from 'idb-keyval'
import { DateTime } from 'luxon'
import type { GETUserInfo } from '~/services/apiClient'
import { getLastConnectedAndDisconnected } from '~/repositories/websocketConnectionState'

const USER_INFO_KEY = 'userInfo'
const USER_INFO_LAST_FETCHED_KEY = 'userInfoLastFetched'

export async function saveToStorage(userInfo: GETUserInfo) {
  // Create a plain object copy without any class instances or circular references
  const userInfoForStorage = JSON.parse(JSON.stringify(userInfo))
  await setMany([
    [USER_INFO_KEY, userInfoForStorage],
    [USER_INFO_LAST_FETCHED_KEY, Date.now()],
  ])
}
interface LoadFromStorageResult {
  userInfo: GETUserInfo | null
  shouldRefresh: boolean
}
export async function loadFromStorage(): Promise<LoadFromStorageResult> {
  const [userInfo, userInfoLastFetched] = await getMany([
    USER_INFO_KEY,
    USER_INFO_LAST_FETCHED_KEY,
  ])
  if (!userInfo) {
    return { userInfo: null, shouldRefresh: true }
  }
  const soketiConnectionInfo = await getLastConnectedAndDisconnected()
  const lastFetched = DateTime.fromMillis(userInfoLastFetched)
  const now = DateTime.now()
  // Force immediate refresh for very old data
  if (lastFetched < now.minus({ days: 2 })) {
    return { userInfo: null, shouldRefresh: true }
  }
  let triggerAsyncRefresh = false
  // Trigger asynchronous refresh for somewhat old data
  if (lastFetched < now.minus({ hours: 6 })) {
    triggerAsyncRefresh = true
  }
  // Trigger asychronous refresh if we've disconnected recently since we loaded
  // data. The point here is if we've been offline we may have missed websocket
  // updates about our user or it's organizations
  else if (
    soketiConnectionInfo.lastDisconnected &&
    soketiConnectionInfo.lastDisconnected > lastFetched
  ) {
    triggerAsyncRefresh = true
  }
  // Trigger async refresh if we connected recently since we loaded
  else if (
    soketiConnectionInfo.lastConnected &&
    // A little buffer so immediate page reloads don't trigger a refresh, although
    // most page reloads would request a refresh
    soketiConnectionInfo.lastConnected.minus({ minutes: 1 }) > lastFetched
  ) {
    triggerAsyncRefresh = true
  }
  if (triggerAsyncRefresh) {
    console.log('Triggering async refresh of user info')
  }
  console.log(
    `Returning cached user info loaded ${lastFetched.toRelative()}. ` +
      `lastDisconnected: ${soketiConnectionInfo.lastDisconnected?.toRelative()} ` +
      `lastConnected: ${soketiConnectionInfo.lastConnected?.toRelative()}`
  )
  return { userInfo, shouldRefresh: triggerAsyncRefresh }
}
export async function clearFromStorage() {
  await delMany([USER_INFO_KEY, USER_INFO_LAST_FETCHED_KEY])
}
